import classnames from 'classnames'
import styles from './HomeProductListWarpBg.module.scss'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import gsap from 'gsap'
import { MorphSVGPlugin } from 'gsap/dist/MorphSVGPlugin'
import { Flip } from 'gsap/dist/Flip'
import { deviceInfo } from '@/utils'

gsap.registerPlugin(MorphSVGPlugin)
gsap.registerPlugin(Flip)

const ORIGINAL_PATH = 'M0 0h324v255H0Z'
export const WARP_BG_ANIMATION_DURATION = process.env.NEXT_PUBLIC_DISABLE_PRELOADER === 'true' ? 0.4 : 1.6

// Small
// const WARP_PATH = 'M324 0C324 0 312 55 312 126.727C312 198.455 324 255 324 255C324 255 267.5 245.016 158 246.5C48.5 247.984 -1.11464e-05 255 -1.11464e-05 255C-1.11464e-05 255 11 194.455 11 126.727C11 59 0 -1.41625e-05 0 -1.41625e-05C0 -1.41625e-05 73.5 10 162.5 10C251.5 10 324 0 324 0Z'

// Large
const WARP_PATH =
  'M324 0C324 0 301 55 301 126.727C301 198.455 324 255 324 255C324 255 272 241.516 162.5 243C53 244.484 -1.11464e-05 255 -1.11464e-05 255C-1.11464e-05 255 25 194.455 25 126.727C25 59 0 -1.41625e-05 0 -1.41625e-05C0 -1.41625e-05 73.5 16.5 162.5 16.5C251.5 16.5 324 0 324 0Z'

const HomeProductListWarpBg = forwardRef(({ className }, ref) => {
  const $warpPath = useRef()
  const $rectPath = useRef()
  const $rectPathOriginal = useRef()
  const tl = useRef()
  const $container = useRef()
  const [render, setRender] = useState(true)

  const warpBoxAnimation = onComplete => {
    if (!$rectPath.current || !$warpPath.current) return

    if (tl.current) tl.current.kill()

    tl.current = gsap.timeline({
      onComplete: () => {
        if (onComplete) onComplete()
      },
    })

    tl.current.to($rectPath.current, {
      duration: WARP_BG_ANIMATION_DURATION * 0.5,
      ease: 'Power4.easeIn',
      morphSVG: $warpPath.current,
    })

    tl.current.to($rectPath.current, {
      duration: WARP_BG_ANIMATION_DURATION * 0.5,
      ease: 'Power4.easeOut',
      morphSVG: $rectPathOriginal.current,
    })
  }

  const shrinkBoxAnimation = () => {
    const $mainTransitionBox = document.getElementById(`transitionBox${1}`)

    if (!$container.current || !$mainTransitionBox) return

    gsap.killTweensOf($container.current)

    const isSafariDesktop = deviceInfo?.isSafariDesktop

    let duration = WARP_BG_ANIMATION_DURATION * 0.5
    let delay = WARP_BG_ANIMATION_DURATION * 0.5
    if (isSafariDesktop) {
      duration = 0.001
      delay = 0.001
    }

    Flip.fit($container.current, $mainTransitionBox, {
      duration,
      ease: 'Power4.easeOut',
      scale: true,
      delay,
    })
  }

  const animateOut = onComplete => {
    shrinkBoxAnimation()
    warpBoxAnimation(onComplete)
  }

  const visuallyHide = onComplete => {
    const isSafariDesktop = deviceInfo?.isSafariDesktop

    gsap.to($container.current, {
      opacity: 0,
      duration: isSafariDesktop ? 0.001 : 0.3,
      onComplete: () => {
        if (onComplete) onComplete()
      },
    })
  }

  useImperativeHandle(ref, () => ({
    animateOut,
    setRender,
    visuallyHide,
  }))

  if (!render) return null

  return (
    <div
      className={classnames(styles.HomeProductListWarpBg, className)}
      ref={$container}
    >
      <svg
        className={styles.svg}
        viewBox="0 0 324 255"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <path
          d={WARP_PATH}
          ref={$warpPath}
          className={styles.warpPath}
        />
        <path
          d={ORIGINAL_PATH}
          ref={$rectPathOriginal}
          className={styles.rectPathOriginal}
        />
        <path
          d={ORIGINAL_PATH}
          ref={$rectPath}
          className={styles.rectPath}
        />
      </svg>
    </div>
  )
})

HomeProductListWarpBg.displayName = 'HomeProductListWarpBg'

export default HomeProductListWarpBg
